import { CardContent, CardShell } from '@/components/cards/common';
import NoDataMessage from '@/components/states/NoDataMessage';
import { useEffect, useMemo, useState } from 'react';
import { GenderPlaysResponse, SelectedPlatform } from '@/types/api/gender';
import { useGenderPlaysQuery } from '@/services/revenueApi';
import useFetchQuery from '@/hooks/useFetchQuery';
import { GenderChart } from '@/routes/AudiencePage/charts/GenderChart/index';
import { mixpanelTrack, RevenueDashboardWidget } from '@/utils';
import { MIXPANEL_EVENT } from '@/utils/mixpanelUtils/events';
import { getFirstDisplayPlatform } from '@/utils/platformUtils';

import GenderModal from './GenderModal';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { AUDIENCE_TAB_EXPERIMENTAL_FEATURES_FLAG } from '@/utils/constants';
import { Button, Tooltip } from '@acast-tech/decibel';
import { ChevronRightRegularGlyph } from '@acast-tech/decibel-glyphs';

const GenderCard = ({ className }: { className?: string }) => {
  const { queryResult } = useFetchQuery(useGenderPlaysQuery, undefined, { sources: [] });

  const { data: genderPlays } = queryResult;

  const platforms = useMemo(() => {
    return [...new Set(genderPlays?.plays?.map((play) => play.platform) ?? [])];
  }, [genderPlays]);

  type SelectedPlatform = (typeof platforms)[number];

  const [selectedPlatform, setSelectedPlatform] = useState<SelectedPlatform | null>(
    platforms?.[0] ?? null
  );

  useEffect(() => {
    setSelectedPlatform(
      getFirstDisplayPlatform({ plays: platforms.map((p) => ({ platform: p })) })
    );
  }, [platforms]);

  const handlePlatformChange = (platform: SelectedPlatform) => {
    setSelectedPlatform(platform);

    mixpanelTrack(MIXPANEL_EVENT.audience.demographics.platformRadioSelected, {
      page: 'audience',
      widget: RevenueDashboardWidget.GenderDemographics,
      platform: platform,
      demographics: 'gender',
    });
  };

  return (
    <CardShell className={className} title="Gender">
      <CardContent
        {...queryResult}
        renderData={(genderData: GenderPlaysResponse) => (
          <Data
            data={genderData}
            selectedPlatform={selectedPlatform}
            handlePlatformChange={handlePlatformChange}
          />
        )}
      />
    </CardShell>
  );
};

const Data = ({
  data,
  selectedPlatform,
  handlePlatformChange,
}: {
  data: GenderPlaysResponse;
  selectedPlatform: SelectedPlatform<typeof data> | null;
  handlePlatformChange: (platform: SelectedPlatform<typeof data>) => void;
}) => {
  const ldClient = useLDClient();
  const audienceTabExperimentalFeaturesEnabled =
    ldClient?.variation(AUDIENCE_TAB_EXPERIMENTAL_FEATURES_FLAG, false) ?? false;

  if (data?.plays?.length < 1 || selectedPlatform === null) {
    return <NoDataMessage dataType="gender data" />;
  }

  return (
    <div className="mt-6 flex flex-col gap-4 justify-between">
      <GenderChart
        data={data}
        selectedPlatform={selectedPlatform}
        handlePlatformChange={handlePlatformChange}
      />
      {audienceTabExperimentalFeaturesEnabled ? (
        <GenderModal />
      ) : (
        <div className="flex flex-row justify-end self-end">
          <Tooltip title="Coming soon" placement="bottom-end">
            <span>
              <Button
                className="self-right"
                variant="tertiary"
                disabled={true}
                glyphEnd={ChevronRightRegularGlyph}
              >
                More details
              </Button>
            </span>
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export default GenderCard;
